import { z } from "zod";

import {
  PersonaBriefGenderSchema,
  PersonaBriefHookPackageSchema,
  type PersonaBriefGender,
  type PersonaBriefHookPackage,
} from "@nexus/contracts";

import { authFetch } from "@/lib/api-client";

export { PersonaBriefGenderSchema, PersonaBriefHookPackageSchema };
export type { PersonaBriefGender, PersonaBriefHookPackage } from "@nexus/contracts";

export const PersonaBriefVersionSourceSchema = z.enum([
  "GENERATED",
  "MANUAL",
  "AI_EDIT",
  "RESTORED",
]);
export type PersonaBriefVersionSource = z.infer<typeof PersonaBriefVersionSourceSchema>;

export const PersonaBriefBrandDtoSchema = z.object({
  productLink: z.string().nullable(),
  font: z.string().nullable(),
  logo: z.string().nullable(),
  disclaimer: z.string().nullable(),
});
export type PersonaBriefBrandDto = z.infer<typeof PersonaBriefBrandDtoSchema>;

export const PersonaBriefHookDtoSchema = z.object({
  id: z.string(),
  hookIndex: z.number(),
  direction: z.string(),
  line: z.string(),
});
export type PersonaBriefHookDto = z.infer<typeof PersonaBriefHookDtoSchema>;

export const PersonaBriefDtoSchema = z.object({
  id: z.string(),
  personaOpportunityId: z.string(),
  slotIndex: z.number(),
  version: z.number(),
  source: PersonaBriefVersionSourceSchema,
  videoName: z.string(),
  duration: z.string(),
  hookPackage: PersonaBriefHookPackageSchema,
  selectedHookIndex: z.number().int().min(1).max(4),
  isFavorite: z.boolean(),
  brand: PersonaBriefBrandDtoSchema,
  hooks: z.array(PersonaBriefHookDtoSchema),
  concept: z.object({
    direction: z.string(),
    line: z.string(),
  }),
  cta: z.object({
    direction: z.string(),
    line: z.string(),
  }),
  creatorProfile: z.object({
    ageRange: z.string(),
    gender: PersonaBriefGenderSchema,
    preferences: z.string().nullable(),
  }),
  notes: z.string().nullable(),
  creativeExampleUrl: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PersonaBriefDto = z.infer<typeof PersonaBriefDtoSchema>;

export const PersonaBriefFavoriteDtoSchema = z.object({
  id: z.string().min(1),
  isFavorite: z.boolean(),
});
export type PersonaBriefFavoriteDto = z.infer<typeof PersonaBriefFavoriteDtoSchema>;

const PersonaBriefListSchema = z.array(PersonaBriefDtoSchema);

export type PersonaBriefContentPatch =
  | { target: "videoName"; value: string }
  | { target: "duration"; value: string }
  | { target: "ageRange"; value: string }
  | { target: "gender"; value: PersonaBriefGender }
  | { target: "hookPackage"; value: PersonaBriefHookPackage }
  | { target: "selectedHookIndex"; value: number }
  | { target: "hook"; hookIndex: number; field: "direction" | "line"; value: string }
  | { target: "concept"; field: "direction" | "line"; value: string }
  | { target: "cta"; field: "direction" | "line"; value: string }
  | { target: "notes"; value: string }
  | { target: "preferences"; value: string }
  | { target: "productLink" | "font" | "logo" | "disclaimer"; value: string | null }
  | { target: "resetBrand" };

export interface PersonaBriefVersionEntryDto {
  id: string;
  version: number;
  source: PersonaBriefVersionSource;
  changeLabel: string | null;
  instruction: string | null;
  restoredFromVersion: number | null;
  createdAt: string;
  createdBy: string | null;
}

export interface PersonaBriefVersionHistoryDto {
  briefId: string;
  versions: PersonaBriefVersionEntryDto[];
  activeVersion: number | null;
  version?: number;
  brief?: PersonaBriefDto;
}

const PersonaBriefVersionEntryDtoSchema = z.object({
  id: z.string(),
  version: z.number(),
  source: PersonaBriefVersionSourceSchema,
  changeLabel: z.string().nullable(),
  instruction: z.string().nullable(),
  restoredFromVersion: z.number().nullable(),
  createdAt: z.string(),
  createdBy: z.string().nullable(),
});

const PersonaBriefVersionHistoryDtoSchema = z.object({
  briefId: z.string(),
  versions: z.array(PersonaBriefVersionEntryDtoSchema),
  activeVersion: z.number().nullable(),
  version: z.number().optional(),
  brief: PersonaBriefDtoSchema.optional(),
});

/**
 * List latest briefs for a persona opportunity set.
 * `GET /persona-opportunity-sets/:setId/briefs`
 */
export async function fetchPersonaOpportunitySetBriefs(
  setId: string,
): Promise<PersonaBriefDto[]> {
  const { data } = await authFetch<unknown>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/briefs`,
  );
  return PersonaBriefListSchema.parse(data ?? []);
}

/**
 * Persist a manual title, cell, dropdown, or brand edit (no AI).
 * `PATCH /persona-opportunity-sets/:setId/briefs/:briefId/content`
 */
export async function patchPersonaBriefContent(
  setId: string,
  briefId: string,
  tenantId: string,
  patch: PersonaBriefContentPatch,
): Promise<PersonaBriefDto> {
  const { data } = await authFetch<unknown>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/briefs/${encodeURIComponent(briefId)}/content`,
    {
      method: "PATCH",
      body: JSON.stringify({ tenantId, ...patch }),
    },
  );
  return PersonaBriefDtoSchema.parse(data);
}

/**
 * Persist the brief identity star (does not append a version).
 * `PATCH /persona-opportunity-sets/:setId/briefs/:briefId/favorite`
 */
export async function togglePersonaBriefFavorite(
  setId: string,
  briefId: string,
  tenantId: string,
  isFavorite: boolean,
): Promise<PersonaBriefFavoriteDto> {
  const { data } = await authFetch<unknown>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/briefs/${encodeURIComponent(briefId)}/favorite`,
    {
      method: "PATCH",
      body: JSON.stringify({ tenantId, isFavorite }),
    },
  );
  return PersonaBriefFavoriteDtoSchema.parse(data);
}

/**
 * List version rail entries (newest first), optionally overlay a version snapshot.
 * `GET /persona-opportunity-sets/:setId/briefs/:briefId/versions`
 */
export async function fetchPersonaBriefVersionHistory(
  setId: string,
  briefId: string,
  version?: number,
): Promise<PersonaBriefVersionHistoryDto> {
  const query = version != null ? `?version=${encodeURIComponent(String(version))}` : "";
  const { data } = await authFetch<unknown>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/briefs/${encodeURIComponent(briefId)}/versions${query}`,
  );
  return PersonaBriefVersionHistoryDtoSchema.parse(data);
}

/**
 * Append a Restored copy of the selected version.
 * `POST /persona-opportunity-sets/:setId/briefs/:briefId/versions/:version/restore`
 */
export async function restorePersonaBriefVersion(
  setId: string,
  briefId: string,
  version: number,
  tenantId: string,
): Promise<PersonaBriefDto> {
  const { data } = await authFetch<unknown>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/briefs/${encodeURIComponent(briefId)}/versions/${encodeURIComponent(String(version))}/restore`,
    {
      method: "POST",
      body: JSON.stringify({ tenantId }),
    },
  );
  return PersonaBriefDtoSchema.parse(data);
}

export type PersonaBriefAiEditCell =
  | { target: "hook"; hookIndex: number; field: "direction" | "line" }
  | { target: "concept"; field: "direction" | "line" }
  | { target: "cta"; field: "direction" | "line" }
  | { target: "notes" }
  | { target: "preferences" };

export interface AiEditPersonaBriefsRequest {
  tenantId: string;
  setId: string;
  briefIds: string[];
  instruction: string;
  idempotencyKey: string;
  cell?: PersonaBriefAiEditCell;
}

/**
 * Amber Path: rewrite one or more persona briefs with an instruction.
 * `POST /persona-opportunity-sets/:setId/briefs/ai-edit`
 */
export async function aiEditPersonaBriefs(
  body: AiEditPersonaBriefsRequest,
): Promise<{ correlationId: string }> {
  const { correlationId } = await authFetch<void>(
    `/persona-opportunity-sets/${encodeURIComponent(body.setId)}/briefs/ai-edit`,
    {
      method: "POST",
      body: JSON.stringify({
        tenantId: body.tenantId,
        setId: body.setId,
        briefIds: body.briefIds,
        instruction: body.instruction,
        idempotencyKey: body.idempotencyKey,
        ...(body.cell ? { cell: body.cell } : {}),
      }),
    },
  );
  if (!correlationId) {
    throw new Error("AI edit response missing correlationId");
  }
  return { correlationId };
}

export interface DeletePersonaBriefsResult {
  deletedIds: string[];
}

/**
 * Soft-delete selected persona briefs.
 * `POST /persona-opportunity-sets/:setId/briefs/delete`
 */
export async function deletePersonaBriefs(
  setId: string,
  body: { tenantId: string; idempotencyKey: string; briefIds: string[] },
): Promise<DeletePersonaBriefsResult> {
  const { data } = await authFetch<DeletePersonaBriefsResult>(
    `/persona-opportunity-sets/${encodeURIComponent(setId)}/briefs/delete`,
    {
      method: "POST",
      body: JSON.stringify(body),
    },
  );
  if (!data) {
    throw new Error("Delete briefs response missing data");
  }
  return data;
}
